import { ParentSize } from '@visx/responsive'
import { cloneDeep } from 'lodash'
import moment from 'moment'
import { useMemo, useRef, useState } from 'react'
import AttributionSelect from './AttributionSelect'
import Spinner from 'app/components/Spinner'
import DateGroupSelector from 'app/components/viz/DateGroupSelector'
import GraphHeader from 'app/components/viz/GraphHeader'
import BarStackGraph from 'app/components/viz/graphs/BarStackGraph'
import { PeriodTrendList } from './PeriodTrendList'
import type { AttributionDayType, AttributionRowType } from 'app/types/attribution'
import { useUser } from 'app/utils'
import { useIsMobile } from 'app/hooks/useMediaQuery'
import { CHANNEL_HUES, SMC_CHANNEL_GROUP_HUES } from 'lib/chartColors'
import { isSMCOrg } from 'lib/isSMCOrg'

export type LifecycleFilter = `Leads` | `Qualified Leads` | `Appointments Scheduled` | `Patients`

type AttributionGraphProps = {
  dayMetrics?: AttributionDayType[]
  channelRows?: AttributionRowType[]
  isLoading: boolean
  filter?: LifecycleFilter
  onFilterChange?: (filter: LifecycleFilter) => void
}

export default function AttributionGraph({
  dayMetrics,
  channelRows,
  isLoading,
  filter: controlledFilter,
  onFilterChange,
}: AttributionGraphProps) {
  const user = useUser()
  const isMobile = useIsMobile()
  const [internalFilter, setInternalFilter] = useState<LifecycleFilter>(`Leads`)

  // Use controlled filter if provided, otherwise use internal state
  const filter = controlledFilter ?? internalFilter
  const setFilter = onFilterChange ?? setInternalFilter
  const channelTotals = useMemo(() => {
    if (!dayMetrics) return {}
    return {
      leads: dayMetrics.reduce(
        (acc, day) => {
          day.leadsByChannel.forEach((channel) => {
            acc[channel.channel] = (acc[channel.channel] ?? 0) + channel.count
          })
          return acc
        },
        {} as { [key: string]: number },
      ),
      mqls: dayMetrics.reduce(
        (acc, day) => {
          day.mqlsByChannel.forEach((channel) => {
            acc[channel.channel] = (acc[channel.channel] ?? 0) + channel.count
          })
          return acc
        },
        {} as { [key: string]: number },
      ),
      appointments: dayMetrics.reduce(
        (acc, day) => {
          day.appointmentsByChannel.forEach((channel) => {
            acc[channel.channel] = (acc[channel.channel] ?? 0) + channel.count
          })
          return acc
        },
        {} as { [key: string]: number },
      ),
      patients: dayMetrics.reduce(
        (acc, day) => {
          day.patientsByChannel.forEach((channel) => {
            acc[channel.channel] = (acc[channel.channel] ?? 0) + channel.count
          })
          return acc
        },
        {} as { [key: string]: number },
      ),
    }
  }, [dayMetrics])
  const channelTotalsByFilter = useMemo(() => {
    // Use dayMetrics byChannel if available, otherwise fall back to channelRows
    if (channelTotals && Object.keys(channelTotals.leads ?? {}).length > 0) {
      switch (filter) {
        case `Leads`:
          return channelTotals.leads
        case `Qualified Leads`:
          return channelTotals.mqls
        case `Appointments Scheduled`:
          return channelTotals.appointments
        case `Patients`:
          return channelTotals.patients
        default:
          return {}
      }
    }
    if (!channelRows?.length) return {}
    return channelRows.reduce(
      (acc, row) => {
        const key = row.bucket
        switch (filter) {
          case `Leads`:
            acc[key] = row.leads
            break
          case `Qualified Leads`:
            acc[key] = row.mqls
            break
          case `Appointments Scheduled`:
            acc[key] = row.appointments
            break
          case `Patients`:
            acc[key] = row.patients
            break
        }
        return acc
      },
      {} as { [key: string]: number },
    )
  }, [channelTotals, channelRows, filter])
  const graphContainerRef = useRef<HTMLDivElement>(null)
  const [barsGroupedBy, setBarsGroupedBy] = useState<`days` | `weeks` | `months`>(`weeks`)
  const groups:
    | {
        leadsByChannel: { day: string; values: { [key: string]: number } }[]
        mqlsByChannel: { day: string; values: { [key: string]: number } }[]
        appointmentsByChannel: { day: string; values: { [key: string]: number } }[]
        patientsByChannel: { day: string; values: { [key: string]: number } }[]
      }
    | undefined = useMemo(() => {
    if (typeof dayMetrics === `undefined`)
      return { leadsByChannel: [], mqlsByChannel: [], appointmentsByChannel: [], patientsByChannel: [] }
    const effectiveBarsGroupedBy =
      barsGroupedBy !== `days` && dayMetrics.length <= 7
        ? `days`
        : barsGroupedBy === `days` && dayMetrics.length > 90
          ? `weeks`
          : barsGroupedBy === `weeks` && dayMetrics.length > 630
            ? `months`
            : barsGroupedBy
    if (effectiveBarsGroupedBy === `days`) {
      return {
        leadsByChannel: dayMetrics?.map((day) => ({
          day: moment(day.bucket).format(`MMM D`),
          values: day.leadsByChannel.reduce((acc, current) => ({ ...acc, [current.channel]: current.count }), {}),
        })),
        mqlsByChannel: dayMetrics?.map((day) => ({
          day: moment(day.bucket).format(`MMM D`),
          values: day.mqlsByChannel.reduce((acc, current) => ({ ...acc, [current.channel]: current.count }), {}),
        })),
        appointmentsByChannel: dayMetrics?.map((day) => ({
          day: moment(day.bucket).format(`MMM D`),
          values: day.appointmentsByChannel.reduce(
            (acc, current) => ({ ...acc, [current.channel]: current.count }),
            {},
          ),
        })),
        patientsByChannel: dayMetrics?.map((day) => ({
          day: moment(day.bucket).format(`MMM D`),
          values: day.patientsByChannel.reduce((acc, current) => ({ ...acc, [current.channel]: current.count }), {}),
        })),
      }
    }

    //IMPORTANT: need to make a copy of the array otherwise the reduce will mutate the original array!
    const copyOfDayMetrics = cloneDeep(dayMetrics)

    if (effectiveBarsGroupedBy === `weeks` || effectiveBarsGroupedBy === `months`) {
      const groups = copyOfDayMetrics.reduce(
        (acc, day) => {
          const interval =
            effectiveBarsGroupedBy === `weeks`
              ? moment(day.bucket).startOf(`week`).format(`MM/DD/YY`)
              : moment(day.bucket).startOf(`month`).format(`MM/DD/YY`)

          if (acc[interval]) {
            acc[interval].leadsByChannel = [...acc[interval].leadsByChannel, ...day.leadsByChannel].reduce(
              (acc, current) => {
                const existing = acc.find((a) => a.channel === current.channel)
                if (existing) {
                  existing.count += current.count
                } else {
                  acc.push(current)
                }
                return acc
              },
              [] as { channel: string; count: number }[],
            )
            acc[interval].mqlsByChannel = [...acc[interval].mqlsByChannel, ...day.mqlsByChannel].reduce(
              (acc, current) => {
                const existing = acc.find((a) => a.channel === current.channel)
                if (existing) {
                  existing.count += current.count
                } else {
                  acc.push(current)
                }
                return acc
              },
              [] as { channel: string; count: number }[],
            )
            acc[interval].appointmentsByChannel = [
              ...acc[interval].appointmentsByChannel,
              ...day.appointmentsByChannel,
            ].reduce(
              (acc, current) => {
                const existing = acc.find((a) => a.channel === current.channel)
                if (existing) {
                  existing.count += current.count
                } else {
                  acc.push(current)
                }
                return acc
              },
              [] as { channel: string; count: number }[],
            )
            acc[interval].patientsByChannel = [...acc[interval].patientsByChannel, ...day.patientsByChannel].reduce(
              (acc, current) => {
                const existing = acc.find((a) => a.channel === current.channel)
                if (existing) {
                  existing.count += current.count
                } else {
                  acc.push(current)
                }
                return acc
              },
              [] as { channel: string; count: number }[],
            )
          } else {
            acc[interval] = {
              bucket: interval,
              leadsByChannel: day.leadsByChannel,
              mqlsByChannel: day.mqlsByChannel,
              appointmentsByChannel: day.appointmentsByChannel,
              patientsByChannel: day.patientsByChannel,
            }
          }
          return acc
        },
        {} as {
          [key: string]: {
            bucket: string
            leadsByChannel: { channel: string; count: number }[]
            mqlsByChannel: { channel: string; count: number }[]
            appointmentsByChannel: { channel: string; count: number }[]
            patientsByChannel: { channel: string; count: number }[]
          }
        },
      )

      const leads = Object.values(groups).map((group) => ({
        day:
          effectiveBarsGroupedBy === `weeks`
            ? moment(group.bucket).format(`MMM D`)
            : moment(group.bucket).format(`MMM YYYY`),
        tooltipDisplay:
          effectiveBarsGroupedBy === `weeks`
            ? `${moment(group.bucket).format(`MMM D`)} - ${moment(group.bucket).endOf(`week`).format(`MMM D`)}`
            : moment(group.bucket).format(`MMMM YYYY`),
        values: group.leadsByChannel.reduce((acc, current) => ({ ...acc, [current.channel]: current.count }), {}),
      }))
      const mqls = Object.values(groups).map((group) => ({
        day:
          effectiveBarsGroupedBy === `weeks`
            ? moment(group.bucket).format(`MMM D`)
            : moment(group.bucket).format(`MMM YYYY`),
        tooltipDisplay:
          effectiveBarsGroupedBy === `weeks`
            ? `${moment(group.bucket).format(`MMM D`)} - ${moment(group.bucket).endOf(`week`).format(`MMM D`)}`
            : moment(group.bucket).format(`MMMM YYYY`),
        values: group.mqlsByChannel.reduce((acc, current) => ({ ...acc, [current.channel]: current.count }), {}),
      }))
      const appointments = Object.values(groups).map((group) => ({
        day:
          effectiveBarsGroupedBy === `weeks`
            ? moment(group.bucket).format(`MMM D`)
            : moment(group.bucket).format(`MMM YYYY`),
        tooltipDisplay:
          effectiveBarsGroupedBy === `weeks`
            ? `${moment(group.bucket).format(`MMM D`)} - ${moment(group.bucket).endOf(`week`).format(`MMM D`)}`
            : moment(group.bucket).format(`MMMM YYYY`),
        values: group.appointmentsByChannel.reduce(
          (acc, current) => ({ ...acc, [current.channel]: current.count }),
          {},
        ),
      }))
      const patients = Object.values(groups).map((group) => ({
        day:
          effectiveBarsGroupedBy === `weeks`
            ? moment(group.bucket).format(`MMM D`)
            : moment(group.bucket).format(`MMM YYYY`),
        tooltipDisplay:
          effectiveBarsGroupedBy === `weeks`
            ? `${moment(group.bucket).format(`MMM D`)} - ${moment(group.bucket).endOf(`week`).format(`MMM D`)}`
            : moment(group.bucket).format(`MMMM YYYY`),
        values: group.patientsByChannel.reduce((acc, current) => ({ ...acc, [current.channel]: current.count }), {}),
      }))
      return {
        leadsByChannel: leads,
        mqlsByChannel: mqls,
        appointmentsByChannel: appointments,
        patientsByChannel: patients,
      }
    }
  }, [dayMetrics, barsGroupedBy])

  const graphData = useMemo(() => {
    switch (filter) {
      case `Leads`:
        return groups?.leadsByChannel
      case `Qualified Leads`:
        return groups?.mqlsByChannel
      case `Appointments Scheduled`:
        return groups?.appointmentsByChannel
      case `Patients`:
        return groups?.patientsByChannel
      default:
        return []
    }
  }, [filter, groups])

  if (!user) return null

  const handleFilterChange = (newFilter: LifecycleFilter) => {
    setFilter(newFilter)
  }

  return (
    <div>
      <GraphHeader
        leftSection={[
          {
            title: `Channel Trending`,
            count: null,
          },
        ]}
        rightSection={[
          <div key="0" className="flex items-center">
            <span className="mr-2 text-ui-xs text-default">Lifecycle:</span>
            <AttributionSelect key={1} defaultFilter={filter} onFilterChange={handleFilterChange} />
          </div>,
          <DateGroupSelector
            key={2}
            defaultGroup={barsGroupedBy}
            onGroupChange={(group) => setBarsGroupedBy(group)}
            disableDays={dayMetrics ? dayMetrics.length > 90 : false}
            disableWeeks={dayMetrics ? dayMetrics.length > 630 || dayMetrics.length <= 7 : false}
            disableMonths={dayMetrics ? dayMetrics.length <= 31 : false}
          />,
        ]}
      />
      {isMobile ? (
        // Time-series bars read poorly on a phone; show the per-period totals as
        // a compact trend list instead. Respects the Lifecycle + Days/Weeks/Months
        // controls above (graphData is already filtered/grouped by them).
        <PeriodTrendList periods={graphData ?? []} />
      ) : (
        <div ref={graphContainerRef} className="relative h-[500px]">
          <ParentSize>
            {() => {
              return (
                <>
                  {isLoading && (
                    <div
                      style={{
                        width: graphContainerRef.current?.clientWidth!,
                        height: graphContainerRef.current?.clientHeight! + 50,
                      }}
                      className="absolute left-0 top-0 z-10 flex items-center justify-center bg-surface bg-opacity-70"
                    >
                      <Spinner size={10} />
                    </div>
                  )}
                  <BarStackGraph
                    allKeys={
                      isSMCOrg(user.orgId)
                        ? [`Digital Marketing`, `Referral Marketing`, `Traditional Marketing`, `Unknown`]
                        : channelTotalsByFilter
                          ? Object.keys(channelTotalsByFilter)
                          : []
                    }
                    width={graphContainerRef.current?.clientWidth!}
                    height={graphContainerRef.current?.clientHeight!}
                    data={graphData ?? []}
                    totals={channelTotalsByFilter}
                    colorMap={isSMCOrg(user.orgId) ? SMC_CHANNEL_GROUP_HUES : CHANNEL_HUES}
                  />
                </>
              )
            }}
          </ParentSize>
        </div>
      )}
    </div>
  )
}
